import { Box, Button, Card, CardContent, Chip, Dialog, DialogContent, DialogTitle, Grid, IconButton, Tab, Tabs, Typography, useTheme } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { DataGrid } from "@mui/x-data-grid";
import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/header";
import TrafficLightDetail from "../components/trafficLightDetail";
import { tokens } from "../theme/theme";
import { UserContext } from "../theme/userContext";

const SIGNAL_COLORS = { GREEN: '#4caf50', YELLOW: '#ff9800', RED: '#f44336' };
const SIGNAL_LABELS = { GREEN: 'Grün', YELLOW: 'Gelb', RED: 'Rot' };

function SignalCard({ label, count, color, active, onClick }) {
    return (
        <Card
            onClick={onClick}
            sx={{
                backgroundColor: color, color: '#fff', minWidth: 120, cursor: 'pointer',
                border: active ? '3px solid #fff' : '3px solid transparent',
                opacity: active ? 1 : 0.75,
                transition: 'all 0.2s',
                '&:hover': { opacity: 1, transform: 'scale(1.03)' },
            }}
        >
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>{count}</Typography>
                <Typography variant="body1">{label}</Typography>
            </CardContent>
        </Card>
    );
}

function SignalDot({ signal }) {
    const color = SIGNAL_COLORS[signal] || '#9e9e9e';
    return <Box sx={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: color, border: '2px solid rgba(255,255,255,0.3)' }} />;
}

const TrafficLightReport = () => {
    const { user } = useContext(UserContext);
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [signalFilter, setSignalFilter] = useState('ALL'); // ALL, GREEN, YELLOW, RED
    const [tabValue, setTabValue] = useState(0);
    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedIdentifier, setSelectedIdentifier] = useState(null);
    const [selectedName, setSelectedName] = useState('');

    useEffect(() => {
        if (!user?.p_user) return;
        const base = process.env.REACT_APP_API_URI;
        fetch(`${base}/api/trafficlight/summary?puser=${user.p_user}`)
            .then(r => r.json())
            .then(data => {
                setSummary(data);
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                setLoading(false);
            });
    }, [user]);

    if (loading) return <Box m="20px"><Header title="AMPEL-REPORT" /><Typography>Laden...</Typography></Box>;
    if (!summary) return <Box m="20px"><Header title="AMPEL-REPORT" /><Typography>Keine Daten verfügbar. Bitte zuerst den wöchentlichen Lauf starten.</Typography></Box>;

    const handleRowClick = (params) => {
        setSelectedIdentifier(params.row.identifier);
        setSelectedName(params.row.name || params.row.identifier);
        setDetailOpen(true);
    };

    const handleSignalCardClick = (signal) => {
        setSignalFilter(prev => prev === signal ? 'ALL' : signal);
    };

    // All positions with filtering
    const allPositions = (summary.positions || []).map((r) => ({
        ...r,
        id: r.portfolio_id || r.identifier,
    }));
    const filteredPositions = signalFilter === 'ALL'
        ? allPositions
        : allPositions.filter(p => p.signal === signalFilter);

    // Main table columns
    const mainColumns = [
        {
            field: 'signal', headerName: 'Ampel', width: 70, align: 'center', headerAlign: 'center',
            renderCell: (params) => <SignalDot signal={params.value} />,
        },
        {
            field: 'identifier', headerName: 'Identifier', width: 120,
            renderCell: (params) => (
                <Button color="inherit" variant="text" size="small" component={Link}
                    to={{ pathname: "/detailview" }}
                    state={{
                        identifier: params.row.identifier,
                        portfolioid: params.row.portfolio_id || null,
                    }}
                    sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                >
                    {params.value}
                </Button>
            ),
        },
        { field: 'name', headerName: 'Name', width: 200 },
        {
            field: 'strategy', headerName: 'Strategie', width: 90, align: 'center', headerAlign: 'center',
            renderCell: (params) => {
                const labels = { growth: 'Growth', dividend: 'Dividende', longterm: 'Langfrist' };
                const colors = { growth: '#2196f3', dividend: '#ff9800', longterm: '#9c27b0' };
                const val = params.value || 'longterm';
                return (
                    <Chip label={labels[val] || val} size="small"
                        sx={{ backgroundColor: colors[val] || '#9e9e9e', color: '#fff', fontWeight: 'bold', fontSize: '0.7rem' }} />
                );
            },
        },
        {
            field: 'portfolio_action_score', headerName: 'Score', width: 80, type: 'number',
            renderCell: (params) => {
                const score = params.value;
                if (score == null) return '';
                const color = SIGNAL_COLORS[params.row.signal] || '#9e9e9e';
                return (
                    <Chip label={Math.round(Number(score))} size="small"
                        sx={{ backgroundColor: color, color: '#fff', fontWeight: 'bold' }} />
                );
            },
        },
        {
            field: 'asset_score', headerName: 'Asset', width: 70, type: 'number',
            valueFormatter: (params) => params.value != null ? Math.round(Number(params.value)) : '',
        },
        {
            field: 'portfolio_overlay', headerName: 'Overlay', width: 75, type: 'number',
            renderCell: (params) => {
                const val = params.value;
                if (val == null || Number(val) === 0) return '';
                return <Typography variant="body2" sx={{ color: '#f44336' }}>{Number(val)}</Typography>;
            },
        },
        {
            field: 'score_delta', headerName: 'Δ Woche', width: 80, type: 'number',
            renderCell: (params) => {
                const delta = params.value;
                if (delta == null) return '—';
                const color = delta > 0 ? '#4caf50' : delta < 0 ? '#f44336' : '#9e9e9e';
                return <Typography variant="body2" sx={{ color, fontWeight: 'bold' }}>{delta > 0 ? '+' : ''}{Math.round(delta)}</Typography>;
            },
        },
        {
            field: 'signal_prev', headerName: 'Vorwoche', width: 90,
            renderCell: (params) => {
                if (!params.value) return '—';
                return <SignalDot signal={params.value} />;
            },
        },
        {
            field: 'coverage_pct', headerName: 'Coverage', width: 90, type: 'number',
            valueFormatter: (params) => params.value != null ? Math.round(Number(params.value)) + '%' : '',
        },
        { field: 'decision_label', headerName: 'Aktion', width: 90 },
        {
            field: 'detail', headerName: 'Detail', width: 80, sortable: false,
            renderCell: (params) => (
                <Button size="small" variant="outlined" color="inherit"
                    onClick={(e) => { e.stopPropagation(); handleRowClick(params); }}
                >
                    Analyse
                </Button>
            ),
        },
    ];

    // Signal changes columns
    const signalChangeColumns = [
        { field: 'signal', headerName: 'Aktuell', width: 70, renderCell: (params) => <SignalDot signal={params.value} /> },
        { field: 'identifier', headerName: 'Identifier', width: 120 },
        { field: 'name', headerName: 'Name', width: 200 },
        { field: 'signal_prev', headerName: 'Vorwoche', width: 80, renderCell: (params) => <SignalDot signal={params.value} /> },
        { field: 'score', headerName: 'Score', width: 80, type: 'number' },
        { field: 'score_prev', headerName: 'Vorher', width: 80, type: 'number' },
        {
            field: 'score_delta', headerName: 'Delta', width: 80, type: 'number',
            renderCell: (params) => {
                const delta = params.value;
                if (delta == null) return '';
                const color = delta > 0 ? '#4caf50' : '#f44336';
                return <Typography sx={{ color, fontWeight: 'bold' }}>{delta > 0 ? '+' : ''}{Math.round(delta)}</Typography>;
            }
        },
    ];

    return (
        <Box m="20px">
            <Header title="AMPEL-REPORT" subtitle="Wöchentliche Ampel-Bewertung aller Common-Stock-Positionen" />

            {/* Summary Cards — klickbar zum Filtern */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item>
                    <SignalCard label="Grün" count={summary.green} color="#4caf50"
                        active={signalFilter === 'GREEN'} onClick={() => handleSignalCardClick('GREEN')} />
                </Grid>
                <Grid item>
                    <SignalCard label="Gelb" count={summary.yellow} color="#ff9800"
                        active={signalFilter === 'YELLOW'} onClick={() => handleSignalCardClick('YELLOW')} />
                </Grid>
                <Grid item>
                    <SignalCard label="Rot" count={summary.red} color="#f44336"
                        active={signalFilter === 'RED'} onClick={() => handleSignalCardClick('RED')} />
                </Grid>
                <Grid item>
                    <Card
                        onClick={() => setSignalFilter('ALL')}
                        sx={{
                            backgroundColor: theme.palette.mode === 'dark' ? colors.primary[400] : colors.primary[100],
                            minWidth: 120, cursor: 'pointer',
                            border: signalFilter === 'ALL' ? '3px solid #2196f3' : '3px solid transparent',
                            transition: 'all 0.2s',
                            '&:hover': { transform: 'scale(1.03)' },
                        }}
                    >
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="h3" sx={{ fontWeight: 'bold' }}>{summary.total}</Typography>
                            <Typography variant="body1">Gesamt</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Tabs: Übersicht / Signalwechsel */}
            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} textColor="secondary" indicatorColor="secondary" sx={{ mb: 2 }}>
                <Tab label={`Alle Positionen (${filteredPositions.length})`} />
                <Tab label={`Signalwechsel (${summary.signalChanges?.length || 0})`} />
            </Tabs>

            {/* Tab 0: Alle Positionen */}
            {tabValue === 0 && (
                <Box>
                    {signalFilter !== 'ALL' && (
                        <Chip
                            label={`Filter: ${SIGNAL_LABELS[signalFilter]} — ${filteredPositions.length} Positionen`}
                            onDelete={() => setSignalFilter('ALL')}
                            sx={{ mb: 1, backgroundColor: SIGNAL_COLORS[signalFilter], color: '#fff' }}
                        />
                    )}
                    <DataGrid
                        rows={filteredPositions}
                        columns={mainColumns}
                        autoHeight
                        density="compact"
                        initialState={{
                            sorting: { sortModel: [{ field: 'portfolio_action_score', sort: 'desc' }] },
                        }}
                        pageSizeOptions={[25, 50, 100]}
                        onRowClick={handleRowClick}
                        sx={{
                            cursor: 'pointer',
                            '& .MuiDataGrid-row:hover': {
                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                            },
                        }}
                    />
                </Box>
            )}

            {/* Tab 1: Signalwechsel */}
            {tabValue === 1 && (
                <Box>
                    {summary.signalChanges?.length > 0 ? (
                        <DataGrid
                            rows={summary.signalChanges.map((r, i) => ({ id: i, ...r }))}
                            columns={signalChangeColumns}
                            autoHeight
                            density="compact"
                            hideFooter
                        />
                    ) : (
                        <Typography sx={{ opacity: 0.6, mt: 2 }}>Keine Signalwechsel diese Woche (Vorwoche fehlt oder identisch).</Typography>
                    )}
                </Box>
            )}

            {/* Detail-Dialog */}
            <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        Ampel-Analyse: {selectedName}
                    </Typography>
                    <IconButton onClick={() => setDetailOpen(false)}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent>
                    {selectedIdentifier && (
                        <Box>
                            <TrafficLightDetail identifier={selectedIdentifier} />
                            <Box sx={{ mt: 2, textAlign: 'right' }}>
                                <Button variant="outlined" component={Link}
                                    to={{ pathname: "/detailview" }}
                                    state={{ identifier: selectedIdentifier }}
                                >
                                    Zur vollständigen Detailansicht
                                </Button>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default TrafficLightReport;
